import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { MoreHorizontal, Network, PencilLine, Trash2 } from 'lucide-react';
import { Button } from '@ui/button.tsx';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@ui/dropdown-menu.tsx';
import { Card, CardContent } from '@ui/card.tsx';
import { Skeleton } from '@ui/skeleton.tsx';
import { useSalesHierarchies } from '@admin/hooks/use-sales-hierarchies.ts';
import { useDeleteSalesHierarchy } from '@admin/hooks/use-delete-sales-hierarchy.ts';
import type { SalesHierarchySalesHierarchyReadModel } from '@r/api/generated/client.schemas.ts';
import { RenameHierarchyDialog } from './rename-hierarchy-dialog.tsx';

type SalesHierarchy = SalesHierarchySalesHierarchyReadModel;

interface HierarchyListProps {
  canManage: boolean;
}

/** Liste des hiérarchies commerciales du workspace, une card par hiérarchie. */
export function HierarchyList({ canManage }: HierarchyListProps) {
  const { t } = useTranslation();
  const { hierarchies, isHierarchiesLoading } = useSalesHierarchies();
  const { handleDelete } = useDeleteSalesHierarchy();
  const navigate = useNavigate();
  const [renamingHierarchy, setRenamingHierarchy] = useState<SalesHierarchy | null>(null);
  const [isRenameDialogOpen, setIsRenameDialogOpen] = useState(false);

  const handleRename = (hierarchy: SalesHierarchy) => {
    setRenamingHierarchy(hierarchy);
    setIsRenameDialogOpen(true);
  };

  const handleConsult = (hierarchy: SalesHierarchy) => {
    navigate({ to: '/settings/hierarchy/$id', params: { id: String(hierarchy.id) } });
  };

  if (isHierarchiesLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((key) => (
          <Card key={key}>
            <CardContent className="flex justify-between items-center p-6">
              <div className="grid flex-1 grid-cols-4 gap-6">
                <div className="space-y-2">
                  <Skeleton className="w-24 h-3" />
                  <Skeleton className="w-32 h-5" />
                </div>
                <div className="space-y-2">
                  <Skeleton className="w-28 h-3" />
                  <Skeleton className="w-40 h-5" />
                </div>
                <div className="space-y-2">
                  <Skeleton className="w-24 h-3" />
                  <Skeleton className="w-8 h-5" />
                </div>
                <div className="space-y-2">
                  <Skeleton className="w-20 h-3" />
                  <Skeleton className="w-24 h-5" />
                </div>
              </div>
              <Skeleton className="w-8 h-8 rounded-md" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (!hierarchies || hierarchies.length === 0) {
    return (
      <Card className="w-full">
        <CardContent className="p-6">
          <p className="text-center text-muted-foreground">{t('admin.hierarchy.empty')}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {hierarchies.map((hierarchy) => (
        <Card key={hierarchy.id}>
          <CardContent className="flex justify-between items-center p-6">
            <div className="grid flex-1 grid-cols-4 gap-6">
              <div>
                <p className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">
                  {t('admin.hierarchy.list.name')}
                </p>
                <p className="font-semibold">{hierarchy.name}</p>
              </div>
              <div>
                <p className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">
                  {t('admin.hierarchy.list.parents')}
                </p>
                {hierarchy.parents.length > 0 ? (
                  <p>{hierarchy.parents.map((parent) => parent.user.fullname).join(', ')}</p>
                ) : (
                  <p className="text-muted-foreground">—</p>
                )}
              </div>
              <div>
                <p className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">
                  {t('admin.hierarchy.list.subordinates')}
                </p>
                <p className="font-semibold text-primary">{hierarchy.subordinatesCount}</p>
              </div>
              <div>
                <p className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">
                  {t('admin.hierarchy.list.created.at')}
                </p>
                <p>{new Date(hierarchy.createdAt).toLocaleDateString()}</p>
              </div>
            </div>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="flex h-8 w-8 p-0 data-[state=open]:bg-muted"
                  aria-label={t('admin.hierarchy.list.actions')}
                >
                  <MoreHorizontal className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {canManage && (
                  <DropdownMenuItem onClick={() => handleRename(hierarchy)}>
                    <PencilLine className="mr-2 w-4 h-4" />
                    {t('admin.hierarchy.action.rename')}
                  </DropdownMenuItem>
                )}

                <DropdownMenuItem onClick={() => handleConsult(hierarchy)}>
                  <Network className="mr-2 w-4 h-4" />
                  {t('admin.hierarchy.list.consult')}
                </DropdownMenuItem>

                {canManage && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => handleDelete(hierarchy)}
                      className="text-destructive focus:text-destructive"
                    >
                      <Trash2 className="mr-2 w-4 h-4" />
                      {t('admin.hierarchy.action.delete')}
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </CardContent>
        </Card>
      ))}

      <RenameHierarchyDialog
        hierarchy={renamingHierarchy}
        isOpen={isRenameDialogOpen}
        onOpenChange={setIsRenameDialogOpen}
      />
    </div>
  );
}
